import { HTMLAttributes } from 'react';
import { useRecoilValue } from 'recoil';
import { useConditionalEffect, usePrevious } from 'react-delta';
import classNames from 'classnames';
import ClearIcon from '@mui/icons-material/Cancel';

import * as K from 'common/constants';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { sidebarStatus } from 'components/search/SearchLogsPage/SearchStateManager.state';
import { activeDataSourcesState } from 'components/common/Managers/ViewStateManager.state';
import TruncateWithTooltip from 'components/common/TruncateWithTooltip/TruncateWithTooltip';

import './SourceSelector.scss';

type GetSourcesDataActorProps = HTMLAttributes<HTMLDivElement> & {
  getSourcesData:  () => void;
};


/**
 * This actor component calls the getSourcesData callback after a new search completes.
 *
 * @prop getSourcesData       Callback function that fetches all facet data for the SourceSelector component
 */
function GetSourcesDataActor({ getSourcesData }: GetSourcesDataActorProps): JSX.Element {
  const status = useRecoilValue(sidebarStatus);
  const shouldExecute =
    status !== usePrevious(status)
    && status === K.COMPONENT_STATUS.RESOLVED;

  useConditionalEffect(() => {
    getSourcesData();
  }, shouldExecute);

  return <></>;
}

type SourceSelectorProps = HTMLAttributes<HTMLDivElement> & {
  getSourcesData:  () => void;
  sourceLabelPlural?: string;
  sourceValues: string[];
};

/**
 * This component gives the user an easy way to see and filter available data sources when the user selects EDR or XDR
 * view. This feature reflects that the user is only searching data sources within a data category. The user sees the
 * selected data sources and a count of unique data sources in that category.
 *
 * @prop getSourcesData     Callback function that fetches all facet data for this component
 * @prop sourceLabelPlural  Display label for the sources in the component's header
 * @prop sourceValues       List of unique data sources
 */
function SourceSelector({
  getSourcesData,
  sourceLabelPlural = 'Sources',
  sourceValues,
  ...props
}: SourceSelectorProps): JSX.Element {

  const activeDataSources = useRecoilValue(activeDataSourcesState);
  const setSearchParams = useSetSearchParams();

  let infoSection = <span>{
    sourceValues.length === 1
      ? sourceValues[0]
      : 'All'
  }</span>;
  let countSection = <span className="SourceSelector__SourceValuesCount">{sourceValues.length}</span>;
  if (activeDataSources) {
    infoSection = <TruncateWithTooltip id="data-source" text={activeDataSources} maxLength={22} />;
    countSection =
      <span className="SourceSelector__SourceValuesCount">
        {activeDataSources.split(',').length}
        <ClearIcon
          className="SourceSelector__ClearIcon"
          fontSize="small"
          onClick={() => setSearchParams({ [K.URL_PARAM.DATA_SOURCE]: '' })}
        />
      </span>;
  }

  const sourceSelectorClass = classNames(
    'SourceSelector',
    { 'SourceSelector--cursorPointer': sourceValues.length > 1 },
  );

  return (
    <>
      <div className={sourceSelectorClass} {...props}>
        <div className="SourceSelector__Header">{sourceLabelPlural}</div>
        <div className="SourceSelector__DataCategory">
          {infoSection}
          {countSection}
        </div>
      </div>
      <GetSourcesDataActor getSourcesData={getSourcesData} />
    </>
  );
}

export default SourceSelector;
